/**
 * Portfolio Performance API client — talks to the real portfolio-performance FastAPI
 * service, proxied through platform-api's gateway at `/portfolio-performance/...`.
 *
 * Follows the same convention as AthenaPricingEngine/lib/api/client.ts (itself modeled
 * on UnderwritingEngineV2/lib/controllerApi.ts): axios directly, a Keycloak bearer token
 * attached via UserService, and the backend's `{status, data}` envelope unwrapped here
 * so callers just get `T`.
 */
import axios from "axios";
// @ts-ignore - JS module, no types
import UserService from "../../../../services/UserService";
// eslint-disable-next-line @typescript-eslint/no-var-requires
const serverConfig = require("../../../../config/server.js");

const BASE = `${serverConfig.api_base_url}portfolio-performance`;

interface ApiEnvelope<T> {
  status: string;
  data: T;
}

async function authConfig() {
  const token = await UserService.ensureFreshToken();
  return { headers: { Authorization: `Bearer ${token}` } };
}

/** Drops undefined/empty-string filter values so "All industries"-style
 * selections don't get sent as a literal empty query param. */
function cleanParams(
  params?: Record<string, string | undefined>,
): Record<string, string> | undefined {
  if (!params) return undefined;
  return Object.fromEntries(
    Object.entries(params).filter((entry): entry is [string, string] => {
      const value = entry[1];
      return value != null && value !== "";
    }),
  );
}

export async function apiGet<T>(
  path: string,
  params?: Record<string, string | undefined>,
): Promise<T> {
  const config = await authConfig();
  const response = await axios.get<ApiEnvelope<T>>(`${BASE}${path}`, {
    ...config,
    params: cleanParams(params),
  });
  return response.data.data;
}

export async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const config = await authConfig();
  const response = await axios.post<ApiEnvelope<T>>(`${BASE}${path}`, body, config);
  return response.data.data;
}

export async function apiPut<T>(path: string, body: unknown): Promise<T> {
  const config = await authConfig();
  const response = await axios.put<ApiEnvelope<T>>(`${BASE}${path}`, body, config);
  return response.data.data;
}

/** For file downloads (e.g. CSV export) -- a plain `<a href>` to the backend
 * fails, since the gateway requires the same bearer token apiGet/apiPost
 * attach; this fetches the file as a blob instead. Unlike apiGet/apiPost,
 * the response isn't the `{status, data}` envelope -- it's the raw file. */
export async function apiGetBlob(
  path: string,
  params?: Record<string, string | undefined>,
): Promise<Blob> {
  const config = await authConfig();
  const response = await axios.get(`${BASE}${path}`, {
    ...config,
    params: cleanParams(params),
    responseType: "blob",
  });
  return response.data;
}

export const MOCK_LATENCY_MS = 320;

/** Client-side-only simulation helper, same as risk-pricing-engine's own
 *  lib/api/client.ts -- used by features with no owned backend table to
 *  write to (see lib/api/loss-calibration.ts's usePromoteK). */
export function mockResponse<T>(value: T, latency = MOCK_LATENCY_MS): Promise<T> {
  return new Promise((resolve) => {
    setTimeout(() => resolve(value), latency);
  });
}
